/**
 * /welcome/:offerSku/firm?token= — the one page a BKOS transfer customer is
 * sent to for their firm's details (job mct_b27e5971-bd1). A checkout buyer
 * gives them on /welcome straight after paying; a transfer customer never sees
 * that page, so the Engine emails them this link ("first step: set up your
 * firm details") and keeps reminding them until the details are in
 * (worker/src/transfer-firm-details.ts).
 *
 * Only the firm form: no "payment confirmed", no price, no kickoff link and no
 * Roadmap. Those come in the welcome, which the Engine holds until BKOS has
 * built the workspace. The form is the same one /welcome uses and posts to the
 * same route, so the details land on the BKOS set-up to-do the same way.
 *
 * Identity comes only from the signed token (order + email); nothing rides in
 * the URL in the clear.
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BRAND_NAME } from './branding';
import { useNoIndex } from './lib/useNoIndex';
import { BkosWorkspaceForm } from './WelcomePage';

const API = import.meta.env.VITE_API_BASE_URL as string;
const TSB_LOGO = 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/tsb/logo-light.svg';

interface Workspace { state: string; address: string | null }

export function TransferFirmPage() {
  const { offerSku = '' } = useParams<{ offerSku: string }>();
  const [params] = useSearchParams();
  useNoIndex(true);
  const token = params.get('token');
  const [who, setWho] = useState<{ email: string; order: string } | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [problem, setProblem] = useState<'link' | 'network' | null>(null);

  useEffect(() => {
    document.title = `Your firm details · ${BRAND_NAME}`;
    return () => { document.title = BRAND_NAME; };
  }, []);

  useEffect(() => {
    if (!token) { setProblem('link'); return; }
    let live = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/welcome/${encodeURIComponent(offerSku)}?token=${encodeURIComponent(token)}`);
        const d = await r.json() as { ok: boolean; identity?: { email: string; order?: string | null } | null };
        if (!live) return;
        if (!d.ok || !d.identity?.email || !d.identity.order) { setProblem('link'); return; }
        const id = { email: d.identity.email, order: d.identity.order };
        setWho(id);
        const s = await fetch(`${API}/api/public/welcome/status?order=${encodeURIComponent(id.order)}&email=${encodeURIComponent(id.email)}`);
        const sd = await s.json() as { ok: boolean; workspace?: Workspace | null };
        if (!live) return;
        if (sd.ok && sd.workspace) setWorkspace(sd.workspace);
        else setProblem('link');
      } catch {
        if (live) setProblem('network');
      }
    })();
    return () => { live = false; };
  }, [offerSku, token]);

  return (
    <div style={wrap}>
      <header style={nav}>
        <div style={navInner}>
          <a href="https://www.thesuccessfulbookkeeper.com" style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <img src={TSB_LOGO} alt="The Successful Bookkeeper" style={{ height: 32 }} />
            <span style={bkosMark}>BKOS</span>
          </a>
        </div>
      </header>
      <main style={main}>
        <p style={eyebrow}>Your first step</p>
        <h1 style={h1}>Set up your firm details</h1>
        <p style={lede}>
          We need three things to build your BKOS workspace: your firm's name, the web address you would like
          for it, and the country your firm is based in. It takes about a minute.
        </p>
        <section style={card}>
          {problem === 'link'
            ? <p style={{ margin: 0 }}>This link has expired or is not complete. Reply to the email that brought you here and we'll send you a fresh one, or set it up with you.</p>
            : problem === 'network'
              ? <p style={{ margin: 0 }}>We could not reach our servers. Please check your connection and reload this page.</p>
              : who && workspace
                ? <BkosWorkspaceForm order={who.order} email={who.email} initial={workspace} onState={() => {}} transfer />
                : <p style={{ margin: 0, opacity: 0.75 }}>One moment…</p>}
        </section>
        <p style={fine}>
          Once your details are in, BKOS emails you to activate your workspace, and we send your welcome
          with the link to book your kickoff call with Mitchell.
        </p>
      </main>
    </div>
  );
}

const INK = '#0A0A2E';
const wrap: React.CSSProperties = { minHeight: '100vh', background: '#F8F6F3', color: INK, fontFamily: 'var(--font-body, "Poppins", -apple-system, sans-serif)' };
const nav: React.CSSProperties = { padding: '12px 24px', borderBottom: '1px solid rgba(10,10,46,0.08)', background: '#fff' };
const navInner: React.CSSProperties = { maxWidth: 760, margin: '0 auto', display: 'flex', alignItems: 'center' };
const bkosMark: React.CSSProperties = { fontWeight: 700, letterSpacing: 1.5, fontSize: 13, color: '#fff', background: INK, borderRadius: 6, padding: '4px 8px' };
const main: React.CSSProperties = { maxWidth: 760, margin: '0 auto', padding: '40px 24px 72px' };
const eyebrow: React.CSSProperties = { textTransform: 'uppercase', letterSpacing: 1.2, fontSize: 12, fontWeight: 600, opacity: 0.6, margin: '0 0 10px' };
const h1: React.CSSProperties = { fontFamily: 'var(--font-heading, "Fraunces", Georgia, serif)', fontSize: 'clamp(30px, 4.2vw, 40px)', fontWeight: 600, lineHeight: 1.15, margin: '0 0 16px' };
const lede: React.CSSProperties = { fontSize: 17, lineHeight: 1.6, opacity: 0.85, margin: '0 0 28px' };
const card: React.CSSProperties = { background: '#fff', borderRadius: 10, padding: '24px 28px', marginBottom: 20, borderLeft: '4px solid var(--prog-primary, #FF6B35)', lineHeight: 1.55 };
const fine: React.CSSProperties = { fontSize: 13, opacity: 0.7, margin: '14px 0 0', lineHeight: 1.5 };

export default TransferFirmPage;
